import React, { useCallback, useEffect, useState } from 'react'
import Modal from 'react-bootstrap/Modal'
import { GetAction } from '../../Component/GetAction'
import { PostAction } from '../../Component/PostAction'
import ManageTable from '../ManageTable'
import {
  canAccessAdminFlag,
  hasFullAdminAccess,
  hasModule,
} from '../auth'
import { RequireModule, PageHeader, BTN_PRIMARY } from './shared'

const EMPTY = {
  id: '',
  first_name: '',
  last_name: '',
  email_address: '',
  mobile: '',
  pass_word: '',
  pan_number: '',
  adhar_card: '',
  address_1: '',
  address_2: '',
  city: '',
  country_id: '99',
  country_name: 'India',
  state: '',
  state_name: '',
  zip_no: '',
  roles: [],
}

function canEditRoles() {
  if (hasFullAdminAccess()) return true
  if (canAccessAdminFlag()) return true
  return hasModule('users')
}

function UsersPage() {
  const [rows, setRows] = useState([])
  const [roles, setRoles] = useState([])
  const [countries, setCountries] = useState([])
  const [states, setStates] = useState([])
  const [error, setError] = useState('')
  const [message, setMessage] = useState(null)
  const [loading, setLoading] = useState(true)
  const [showForm, setShowForm] = useState(false)
  const [saving, setSaving] = useState(false)
  const [form, setForm] = useState(EMPTY)

  const actorId = String(sessionStorage.getItem('id') || '')
  const allowRoles = canEditRoles()
  const isEditingSelf =
    Boolean(form.id) && actorId && String(form.id) === actorId
  const rolesReadOnly = !allowRoles || isEditingSelf

  const load = useCallback(async () => {
    setLoading(true)
    const data = await GetAction('get', {}, 'user/userList')
    if (data?.status === 'success' && Array.isArray(data.response)) {
      setRows(data.response)
      setError('')
    } else {
      setError(data?.message || 'Failed to load users.')
      setRows([])
    }
    setLoading(false)
  }, [])

  useEffect(() => {
    load()
  }, [load])

  useEffect(() => {
    ;(async () => {
      const c = await GetAction('get', {}, 'Master/countryList')
      if (c?.status === 'success' && Array.isArray(c.response)) {
        setCountries(c.response)
      }
    })()
  }, [])

  useEffect(() => {
    if (!allowRoles) return
    ;(async () => {
      const r = await GetAction('get', {}, 'roles/roleList')
      if (r?.status === 'success' && Array.isArray(r.response)) {
        setRoles(r.response.filter((x) => Number(x.status) === 1))
      }
    })()
  }, [allowRoles])

  const loadStates = useCallback(async (countryId) => {
    if (!countryId) {
      setStates([])
      return
    }
    const data = await GetAction('get', { country_id: countryId }, 'Master/stateList')
    if (data?.status === 'success' && Array.isArray(data.response)) {
      setStates(data.response)
    } else {
      setStates([])
    }
  }, [])

  useEffect(() => {
    if (showForm) loadStates(form.country_id || '99')
  }, [showForm, form.country_id, loadStates])

  const closeForm = () => {
    setShowForm(false)
    setForm(EMPTY)
  }

  const openNew = () => {
    setForm({ ...EMPTY, roles: [] })
    setShowForm(true)
    setMessage(null)
  }

  const openEdit = (row) => {
    const roleIds = Array.isArray(row.role_ids) && row.role_ids.length
      ? row.role_ids.map(String)
      : Array.isArray(row.roles) && row.roles.length
        ? row.roles.map((r) => String(r.id || r))
        : row.user_role
          ? [String(row.user_role)]
          : []
    setForm({
      id: row.id || '',
      first_name: row.first_name || '',
      last_name: row.last_name || '',
      email_address: row.email_address || '',
      mobile: row.mobile || '',
      pass_word: '',
      pan_number: row.pan_number || '',
      adhar_card: row.adhar_card || '',
      address_1: row.address_1 || row.address1 || '',
      address_2: row.address_2 || row.address2 || '',
      city: row.city || '',
      country_id: row.country_id || '99',
      country_name: row.country_name || 'India',
      state: row.state || '',
      state_name: row.state_name || '',
      zip_no: row.zip_no || '',
      roles: roleIds,
    })
    setShowForm(true)
    setMessage(null)
  }

  const onCountry = (e) => {
    const opt = e.target.selectedOptions[0]
    setForm((p) => ({
      ...p,
      country_id: e.target.value,
      country_name: opt?.getAttribute('data-name') || '',
      state: '',
      state_name: '',
    }))
  }

  const onState = (e) => {
    const opt = e.target.selectedOptions[0]
    setForm((p) => ({
      ...p,
      state: e.target.value,
      state_name: opt?.getAttribute('data-name') || '',
    }))
  }

  const onSubmit = async (e) => {
    e.preventDefault()
    setSaving(true)
    setMessage(null)

    const payload = {
      id: form.id || undefined,
      first_name: form.first_name.trim(),
      last_name: form.last_name.trim(),
      email_address: form.email_address.trim(),
      mobile: form.mobile.trim(),
      pan_number: form.pan_number.trim(),
      adhar_card: form.adhar_card.trim(),
      address_1: form.address_1.trim(),
      address_2: form.address_2.trim(),
      city: form.city.trim(),
      country_id: form.country_id,
      country_name: form.country_name,
      state: form.state,
      state_name: form.state_name,
      zip_no: form.zip_no,
      actor_id: actorId ? Number(actorId) : 0,
    }

    if (!form.id) {
      payload.pass_word = form.pass_word
    } else if (form.pass_word) {
      payload.pass_word = form.pass_word
    }

    if (allowRoles && !isEditingSelf) {
      payload.roles = form.roles.map(Number).filter(Boolean)
      if (payload.roles.length) payload.role = payload.roles[0]
    }

    const data = await PostAction('post', payload, 'user/addUser')
    if (data?.status === 'success') {
      setMessage({ error: false, text: data.message || 'User saved.' })
      closeForm()
      await load()
    } else {
      setMessage({
        error: true,
        text: data?.message || data?.data?.message || 'Failed to save user.',
      })
    }
    setSaving(false)
  }

  const columns = [
    { key: 'index', label: '#', width: '60px' },
    { key: 'name', label: 'Name' },
    { key: 'mobile', label: 'Mobile' },
    { key: 'city', label: 'City' },
    {
      key: 'role_name',
      label: 'Role',
      render: (row) => row.role_name || '—',
    },
    {
      key: 'actions',
      label: 'Actions',
      render: (row) => (
        <button
          type="button"
          className="btn btn-sm btn-outline-primary"
          onClick={(e) => {
            e.preventDefault()
            e.stopPropagation()
            openEdit(row)
          }}
        >
          Edit
        </button>
      ),
    },
  ]

  return (
    <RequireModule moduleKey="users">
      <PageHeader
        title="Users"
        subtitle="Registered members."
        actions={
          <button
            type="button"
            className="btn btn-sm btn-primary"
            style={BTN_PRIMARY}
            onClick={openNew}
          >
            Add User
          </button>
        }
      />
      {message ? (
        <div
          className={`manage-alert ${message.error ? 'manage-alert-err' : 'manage-alert-ok'}`}
        >
          {message.text}
        </div>
      ) : null}
      {error ? <div className="manage-alert manage-alert-err">{error}</div> : null}

      <div className="manage-card mb-4">
        {loading ? (
          <div className="text-center py-4">
            <div className="spinner-border text-warning" role="status" />
          </div>
        ) : (
          <ManageTable
            columns={columns}
            rows={rows}
            searchKeys={['name', 'mobile', 'city', 'role_name']}
          />
        )}
      </div>

      <Modal
        show={showForm}
        onHide={closeForm}
        size="lg"
        backdrop="static"
        centered
        scrollable
      >
        <Modal.Header closeButton>
          <Modal.Title>{form.id ? 'Edit User' : 'Add User'}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <form id="manage-user-form" onSubmit={onSubmit}>
            <div className="row">
              <div className="col-md-6 mb-3">
                <label className="form-label" htmlFor="u_first">
                  First Name
                </label>
                <input
                  id="u_first"
                  className="form-control"
                  required
                  value={form.first_name}
                  onChange={(e) =>
                    setForm((p) => ({ ...p, first_name: e.target.value }))
                  }
                />
              </div>
              <div className="col-md-6 mb-3">
                <label className="form-label" htmlFor="u_last">
                  Last Name
                </label>
                <input
                  id="u_last"
                  className="form-control"
                  required
                  value={form.last_name}
                  onChange={(e) =>
                    setForm((p) => ({ ...p, last_name: e.target.value }))
                  }
                />
              </div>
              <div className="col-md-6 mb-3">
                <label className="form-label" htmlFor="u_email">
                  Email
                </label>
                <input
                  id="u_email"
                  type="email"
                  className="form-control"
                  required
                  value={form.email_address}
                  onChange={(e) =>
                    setForm((p) => ({ ...p, email_address: e.target.value }))
                  }
                />
              </div>
              <div className="col-md-6 mb-3">
                <label className="form-label" htmlFor="u_mobile">
                  Mobile
                </label>
                <input
                  id="u_mobile"
                  className="form-control"
                  required
                  value={form.mobile}
                  onChange={(e) =>
                    setForm((p) => ({ ...p, mobile: e.target.value }))
                  }
                />
              </div>
              <div className="col-md-6 mb-3">
                <label className="form-label" htmlFor="u_pass">
                  Password {form.id ? '(leave blank to keep)' : '*'}
                </label>
                <input
                  id="u_pass"
                  type="password"
                  className="form-control"
                  required={!form.id}
                  value={form.pass_word}
                  onChange={(e) =>
                    setForm((p) => ({ ...p, pass_word: e.target.value }))
                  }
                />
              </div>
              <div className="col-md-6 mb-3">
                <label className="form-label" htmlFor="u_pan">
                  PAN <span className="text-muted">(Optional)</span>
                </label>
                <input
                  id="u_pan"
                  className="form-control"
                  value={form.pan_number}
                  onChange={(e) =>
                    setForm((p) => ({ ...p, pan_number: e.target.value }))
                  }
                />
              </div>
              <div className="col-md-6 mb-3">
                <label className="form-label" htmlFor="u_adhar">
                  Aadhaar <span className="text-muted">(Optional)</span>
                </label>
                <input
                  id="u_adhar"
                  className="form-control"
                  value={form.adhar_card}
                  onChange={(e) =>
                    setForm((p) => ({ ...p, adhar_card: e.target.value }))
                  }
                />
              </div>
              <div className="col-md-6 mb-3">
                <label className="form-label" htmlFor="u_city">
                  City
                </label>
                <input
                  id="u_city"
                  className="form-control"
                  value={form.city}
                  onChange={(e) => setForm((p) => ({ ...p, city: e.target.value }))}
                />
              </div>
              <div className="col-md-6 mb-3">
                <label className="form-label" htmlFor="u_addr1">
                  Address 1
                </label>
                <input
                  id="u_addr1"
                  className="form-control"
                  required
                  value={form.address_1}
                  onChange={(e) =>
                    setForm((p) => ({ ...p, address_1: e.target.value }))
                  }
                />
              </div>
              <div className="col-md-6 mb-3">
                <label className="form-label" htmlFor="u_addr2">
                  Address 2
                </label>
                <input
                  id="u_addr2"
                  className="form-control"
                  value={form.address_2}
                  onChange={(e) =>
                    setForm((p) => ({ ...p, address_2: e.target.value }))
                  }
                />
              </div>
              <div className="col-md-4 mb-3">
                <label className="form-label" htmlFor="u_country">
                  Country
                </label>
                <select
                  id="u_country"
                  className="form-control"
                  value={form.country_id}
                  onChange={onCountry}
                >
                  {countries.map((c) => (
                    <option
                      key={c.country_id}
                      value={c.country_id}
                      data-name={c.name}
                    >
                      {c.name}
                    </option>
                  ))}
                </select>
              </div>
              <div className="col-md-4 mb-3">
                <label className="form-label" htmlFor="u_state">
                  State
                </label>
                <select
                  id="u_state"
                  className="form-control"
                  value={form.state}
                  onChange={onState}
                >
                  <option value="">Select state</option>
                  {states.map((s) => (
                    <option
                      key={s.zone_id}
                      value={s.zone_id}
                      data-name={s.name}
                    >
                      {s.name}
                    </option>
                  ))}
                </select>
              </div>
              <div className="col-md-4 mb-3">
                <label className="form-label" htmlFor="u_zip">
                  PIN / ZIP
                </label>
                <input
                  id="u_zip"
                  className="form-control"
                  value={form.zip_no}
                  onChange={(e) =>
                    setForm((p) => ({ ...p, zip_no: e.target.value }))
                  }
                />
              </div>
              {allowRoles ? (
                <div className="col-12 mb-3">
                  <label className="form-label" htmlFor="u_roles">
                    Roles
                  </label>
                  <select
                    id="u_roles"
                    className="form-control"
                    multiple
                    size={Math.min(6, Math.max(3, roles.length || 3))}
                    disabled={rolesReadOnly}
                    value={form.roles}
                    onChange={(e) => {
                      const selected = Array.from(e.target.selectedOptions).map(
                        (o) => o.value
                      )
                      setForm((p) => ({ ...p, roles: selected }))
                    }}
                  >
                    {roles.map((r) => (
                      <option key={r.id} value={String(r.id)}>
                        {r.name}
                      </option>
                    ))}
                  </select>
                  <div className="form-text">
                    Hold Ctrl/Cmd to select multiple.
                    {isEditingSelf
                      ? ' You cannot change your own roles here.'
                      : null}
                  </div>
                </div>
              ) : null}
            </div>
          </form>
        </Modal.Body>
        <Modal.Footer>
          <button
            type="button"
            className="btn btn-outline-secondary"
            onClick={closeForm}
          >
            Cancel
          </button>
          <button
            type="submit"
            form="manage-user-form"
            className="btn btn-primary"
            style={BTN_PRIMARY}
            disabled={saving}
          >
            {saving ? 'Saving…' : 'Save User'}
          </button>
        </Modal.Footer>
      </Modal>
    </RequireModule>
  )
}

export default UsersPage
